export const WORLD_TERRITORY_NAME_ES = "Todo el mundo";

type SelectionValue = { displayId: string; id: string };
type TerritoryOption = { id: string; name_es: string; displayId: string };

/**
 * Optimiza la selección de territorios al guardar:
 * Si todos los países están seleccionados → reemplazar por solo "Worldwide"
 */
export function optimizeTerritoriesForSave(
  selectedTerritories: SelectionValue[],
  allTerritories: TerritoryOption[]
): SelectionValue[] {
  const worldTerritory = allTerritories.find(
    (opt) => opt.name_es === WORLD_TERRITORY_NAME_ES
  );
  if (!worldTerritory) return selectedTerritories;

  // Contar países (excluyendo "Todo el mundo")
  const totalCountries = allTerritories.filter(
    (t) => t.name_es !== WORLD_TERRITORY_NAME_ES
  ).length;

  // Contar seleccionados (excluyendo "Worldwide")
  const selectedCountries = selectedTerritories.filter(
    (t) => t.id !== worldTerritory.id
  ).length;

  // Si todos los países están seleccionados → solo "Worldwide"
  if (selectedCountries === totalCountries && totalCountries > 0) {
    return [{ id: worldTerritory.id, displayId: worldTerritory.displayId }];
  }

  return selectedTerritories;
}

/**
 * Valida que si "Todo el mundo" está seleccionado, no haya otros territorios.
 * Retorna mensaje de error o null si es válido.
 */
export function validateTerritoriesExclusivity(
  selectedTerritories: SelectionValue[],
  allTerritories: TerritoryOption[]
): string | null {
  const worldTerritory = allTerritories.find(
    (opt) => opt.name_es === WORLD_TERRITORY_NAME_ES
  );
  if (!worldTerritory) return null;

  const hasWorld = selectedTerritories.some((t) => t.id === worldTerritory.id);
  const otherCount = selectedTerritories.filter((t) => t.id !== worldTerritory.id).length;

  if (hasWorld && otherCount > 0) {
    return "No se puede seleccionar 'Todo el mundo' junto con otros territorios";
  }

  return null;
}

export function processTerritoriesSelection(
  newValues: SelectionValue[],
  previousValues: SelectionValue[],
  allOptions: TerritoryOption[]
): SelectionValue[] {
  const worldTerritory = allOptions.find(
    (opt) => opt.name_es === WORLD_TERRITORY_NAME_ES
  );
  if (!worldTerritory) return newValues;

  const worldId = worldTerritory.id;
  const hadWorld = previousValues.some((v) => v.id === worldId);
  const hasWorld = newValues.some((v) => v.id === worldId);

  // Caso 1: "Select All" - se seleccionaron todas las opciones → excluir "Todo el mundo"
  // Solo aplica si hay más de 1 opción (para evitar falso positivo cuando el dropdown está filtrado)
  if (newValues.length === allOptions.length && allOptions.length > 1) {
    return newValues.filter((v) => v.id !== worldId);
  }

  // Caso 2: Usuario seleccionó "Todo el mundo" (no lo tenía antes) → solo "Todo el mundo"
  if (hasWorld && !hadWorld) {
    return newValues.filter((v) => v.id === worldId);
  }

  // Caso 3: Tenía "Todo el mundo" y seleccionó otros → quitar "Todo el mundo"
  if (hadWorld && newValues.length > 1) {
    return newValues.filter((v) => v.id !== worldId);
  }

  return newValues;
}
